import { useEffect } from "react";

import { useAnalytics } from "../../hooks/useAnalytics";

interface DisqualificationScreenProps {
  message?: string | null;
  onBack: () => void;
  onSeekOtherTrials: () => void;
  questionId?: string;
  landingPageId?: string;
  hasMoreStudiesToTry?: boolean;
  isMultiStudyFlow?: boolean;
}

export function DisqualificationScreen({
  message,
  onBack,
  onSeekOtherTrials,
  questionId,
  landingPageId = "default",
  hasMoreStudiesToTry = false,
  isMultiStudyFlow = false,
}: DisqualificationScreenProps) {
  const { track } = useAnalytics();

  // Determine if we should show the seamless multi-study variant
  const showSeamlessVariant = isMultiStudyFlow && hasMoreStudiesToTry;

  // The seamless variant never tells the user they didn't match, so it is not a no-match screen
  useEffect(() => {
    if (!showSeamlessVariant) {
      track("no-match.screen", {
        landing_page_id: landingPageId,
      });
    }
  }, []); // Only track once on mount

  // Function to handle clicking the back button
  const handleBack = () => {
    // Track the back button click
    track("lp.pre_screener.edit_answer_clicked", {
      disqualification_reason: message,
      question_id: questionId,
      landing_page_id: landingPageId,
      has_more_studies: hasMoreStudiesToTry,
      is_multi_study: isMultiStudyFlow,
    });

    // Call the provided onBack function
    onBack();
  };

  const onSearchOtherTrials = () => {
    // Track the seek other trials button click
    track("lp.pre_screener.seek_other_trials_clicked", {
      disqualification_reason: message,
      question_id: questionId,
      landing_page_id: landingPageId,
      has_more_studies: hasMoreStudiesToTry,
      is_multi_study: isMultiStudyFlow,
    });

    // Call the provided onSeekOtherTrials function
    onSeekOtherTrials();
  };

  if (showSeamlessVariant) {
    // Seamless multi-study variant - optimistic and encouraging
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-50 mb-4">
          <svg
            className="h-6 w-6 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Just a few more questions!
        </h3>
        <p className="text-gray-600 mb-4 max-w-md mx-auto">
          We're finding the best match for you. Keep going - you're almost done.
        </p>
        <p className="text-gray-500 text-sm italic mb-6 max-w-md mx-auto">
          Your answers will be verified during on-site screening.
        </p>
        <div className="flex flex-col sm:flex-row-reverse justify-center gap-4">
          {/* Primary CTA - Continue */}
          <button
            type="button"
            onClick={onSearchOtherTrials}
            autoFocus
            className="w-full sm:w-auto px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 inline-flex items-center justify-center"
          >
            Continue
            <svg
              className="ml-2 -mr-1 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>

          {/* Secondary CTA - Edit answer (much more subtle) */}
          <button
            type="button"
            onClick={handleBack}
            className="w-full sm:w-auto px-4 py-2 text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Edit previous answer
          </button>
        </div>
      </div>
    );
  }

  // Default variant - for single-study or no remaining studies
  return (
    <div className="text-center py-8">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-50 mb-4">
        <svg
          className="h-6 w-6 text-amber-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        You aren't suitable for this study, but we have other studies actively
        recruiting.
      </h3>
      <p className="text-gray-600 mb-4 max-w-md mx-auto">{message}</p>
      <p className="text-gray-500 text-sm italic mb-6 max-w-md mx-auto">
        Your answers will be verified during on-site screening.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        {/* Back button */}
        <button
          type="button"
          onClick={handleBack}
          className="w-full sm:w-auto px-6 py-3 border border-primary-600 rounded-md shadow-sm text-base font-medium text-primary-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Made a mistake? Edit your answer
        </button>

        <button
          type="button"
          onClick={onSearchOtherTrials}
          className="w-full sm:w-auto px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 inline-flex items-center justify-center"
        >
          Search other trials
        </button>
      </div>
    </div>
  );
}
